import { getConvexClient, convexRefs } from "../convexClient";

const AUTH_KEY = "xDayAuth";

/**
 * Resolve the active cloud user name. Cloud sync is only used by the
 * authorized account, whose credentials are stored at login.
 */
/** Session token minted by auth:login; required by every data function. */
export function getActiveToken() {
  try {
    const saved = localStorage.getItem(AUTH_KEY);
    if (saved) {
      const auth = JSON.parse(saved);
      if (auth && auth.token) return auth.token;
    }
  } catch (e) {
    // ignore parse errors
  }
  return null;
}

/** True when the error is Convex telling us the session is gone. */
export function isUnauthorizedError(error) {
  const d = error && error.data;
  return Boolean(d && typeof d === "object" && d.code === "unauthorized");
}

/**
 * Verify credentials against Convex and mint a session token. Throws on bad
 * credentials (ConvexError with data.code = "bad_credentials").
 */
export async function cloudLogin(username, passkey) {
  const convex = getConvexClient();
  if (!convex) throw new Error("Convex is not configured");
  return await withTimeout(
    convex.mutation(convexRefs.login, {
      userName: username,
      passkey,
      deviceName: describeDevice(),
      platform: "web",
    }),
    15000,
  );
}

/** Probe a saved token; resolves to the session (or null if invalid). */
export async function cloudMe(token) {
  const convex = getConvexClient();
  if (!convex || !token) return null;
  return await withTimeout(convex.query(convexRefs.me, { token }), 15000);
}

export async function cloudLogout() {
  const convex = getConvexClient();
  const token = getActiveToken();
  if (!convex || !token) return;
  try {
    await withTimeout(convex.mutation(convexRefs.logout, { token }), 8000);
  } catch (e) {
    /* best effort */
  }
}

export async function listCloudSessions() {
  const convex = getConvexClient();
  const token = getActiveToken();
  if (!convex || !token) return [];
  return await convex.query(convexRefs.listSessions, { token });
}

export async function logoutOtherDevices() {
  const convex = getConvexClient();
  const token = getActiveToken();
  if (!convex || !token) return { removed: 0 };
  return await convex.mutation(convexRefs.logoutOthers, { token });
}

function describeDevice() {
  try {
    const ua = navigator.userAgent || "";
    const os = /Windows/.test(ua)
      ? "Windows"
      : /Mac/.test(ua)
        ? "macOS"
        : /Android/.test(ua)
          ? "Android"
          : /Linux/.test(ua)
            ? "Linux"
            : "Web";
    const br = ua.includes("Edg/")
      ? "Edge"
      : ua.includes("Chrome/")
        ? "Chrome"
        : ua.includes("Firefox/")
          ? "Firefox"
          : ua.includes("Safari/")
            ? "Safari"
            : "Browser";
    return `${br} on ${os}`;
  } catch (e) {
    return "Web";
  }
}

function getActiveUserName(fallback) {
  if (fallback) return fallback;
  try {
    const saved = localStorage.getItem(AUTH_KEY);
    if (saved) {
      const auth = JSON.parse(saved);
      if (auth && auth.username) return auth.username;
    }
  } catch (e) {
    // ignore parse errors
  }
  return null;
}

/**
 * Run a Convex call with a timeout so the UI never hangs on a slow network.
 */
function withTimeout(promise, ms, timeoutValue) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      if (timeoutValue !== undefined) resolve(timeoutValue);
      else reject(new Error("timeout"));
    }, ms);
    promise.then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      (e) => {
        clearTimeout(timer);
        reject(e);
      },
    );
  });
}

/**
 * Get current timestamp in ISO format
 */
export function getCurrentTimestamp() {
  return new Date().toISOString();
}

/**
 * Format timestamp for display (e.g., "2 min ago", "1 hour ago")
 */
export function formatTimestampRelative(timestamp) {
  if (!timestamp) return "Never";
  
  const now = new Date();
  const then = new Date(timestamp);
  const diffMs = now - then;
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffSeconds < 60) return "Just now";
  if (diffMinutes < 60) return `${diffMinutes} min ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
  return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
}

/**
 * Format timestamp for display in diff view
 */
export function formatTimestampFull(timestamp) {
  if (!timestamp) return "Unknown";
  const date = new Date(timestamp);
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

/**
 * Check if we can connect to Google Sheets API
 * Returns true if online and API is reachable
 */
export async function checkCloudConnectivity() {
  // First check browser's online status
  if (!navigator.onLine) {
    return { online: false, reason: "browser_offline" };
  }

  const convex = getConvexClient();
  if (!convex) {
    return { online: false, reason: "not_configured" };
  }

  // Then try to actually reach Convex
  try {
    const result = await withTimeout(
      convex.query(convexRefs.ping, {}),
      5000,
      "__timeout__",
    );
    if (result === "__timeout__") {
      return { online: false, reason: "timeout" };
    }
    return { online: true, reason: "connected" };
  } catch (error) {
    return { online: false, reason: "network_error", error: error.message };
  }
}

// ---------- canonicalization for conflict detection ----------
// Local (pre-save) data and cloud (post-save) data differ in ways that are NOT
// real user changes:
//   1. auth-only fields the cloud never stores (passkey, userName),
//   2. server-managed metadata (createdAt / updatedAt / order),
//   3. optional fields the backend defaults when absent (e.g. a conference
//      entry's `theme` becomes "" on the server even if the local object never
//      had the key).
// Comparing raw objects therefore reports a "difference" even when nothing
// meaningful changed, which forced a spurious conflict on every sync/open.
//
// We canonicalize BOTH sides to the same normalized shape — mirroring the
// backend's own normalization (see convex/data.ts writeAll) but dropping the
// metadata — so only genuine, user-visible differences remain. This is
// comparison-only: the actual merge/save still uses the full original data, so
// no field is ever dropped from what gets persisted.
const _asStr = (v) => String(v ?? "");
const _asBool = (v) => v === true || String(v).toLowerCase() === "true";
const _emptyToNull = (v) =>
  v === "" || v === undefined || v === null ? null : v;

const canonGoal = (g = {}) => ({
  id: g.id,
  text: _asStr(g.text),
  completed: _asBool(g.completed),
  deadline: _emptyToNull(g.deadline),
  important: _asBool(g.important),
  completedAt: _emptyToNull(g.completedAt),
});

const canonTab = (t = {}) => ({
  id: t.id,
  name: _asStr(t.name),
  goals: (Array.isArray(t.goals) ? t.goals : []).map(canonGoal),
});

const canonSubGoal = (s = {}) => ({
  id: s.id,
  goalId: s.goalId,
  text: _asStr(s.text),
  completed: _asBool(s.completed),
  deadline: _emptyToNull(s.deadline),
  important: _asBool(s.important),
  completedAt: _emptyToNull(s.completedAt),
});

const canonClip = (c = {}) => ({
  id: c.id,
  title: _asStr(c.title),
  content: _asStr(c.content),
  blocks: Array.isArray(c.blocks)
    ? c.blocks.map((b = {}) => ({
        id: b.id,
        type: _asStr(b.type || "text"),
        text: _asStr(b.text),
        checked: _asBool(b.checked),
      }))
    : null,
});

const canonQuickLink = (c = {}) => ({
  id: c.id,
  title: _asStr(c.title),
  links: (Array.isArray(c.links) ? c.links : []).map((l = {}) => ({
    id: l.id,
    url: _asStr(l.url),
    description: _asStr(l.description),
    completed: _asBool(l.completed),
  })),
  dueDate: _emptyToNull(c.dueDate),
  remember: _asBool(c.remember),
  completed: _asBool(c.completed),
});

const canonRecurrence = (r) => {
  if (!r || !Array.isArray(r.weekdays) || typeof r.endDate !== "string") {
    return null;
  }
  return {
    weekdays: r.weekdays
      .map((d) => Number(d))
      .filter((d) => d >= 0 && d <= 6)
      .sort((a, b) => a - b),
    endDate: r.endDate,
  };
};

const canonRemember = (r = {}) => ({
  id: r.id,
  text: _asStr(r.text),
  completed: _asBool(r.completed),
  dueDate: _emptyToNull(r.dueDate),
  recurrence: canonRecurrence(r.recurrence),
  // completedDates is set-derived; order is not meaningful, so sort for a stable
  // comparison.
  completedDates: (Array.isArray(r.completedDates)
    ? r.completedDates.filter((d) => typeof d === "string")
    : []
  )
    .slice()
    .sort(),
});

const canonEntry = (e = {}) => ({
  id: e.id,
  year: _asStr(e.year),
  dateFrom: _emptyToNull(e.dateFrom),
  dateTo: _emptyToNull(e.dateTo),
  venue: _asStr(e.venue),
  website: _asStr(e.website),
  paperName: _asStr(e.paperName),
  paperType: _asStr(e.paperType || "full"),
  paperTypeOther: _asStr(e.paperTypeOther),
  submissionDeadline: _emptyToNull(e.submissionDeadline),
  authors: _asStr(e.authors),
  details: _asStr(e.details),
  theme: _asStr(e.theme),
  fields: (Array.isArray(e.fields) ? e.fields : [])
    .map((t) => _asStr(t).trim())
    .filter(Boolean),
  live: e.live === false ? false : true,
  submitted: _asBool(e.submitted),
  remember: e.remember === true,
  decisionDate: _emptyToNull(e.decisionDate),
  outcome: ["pending", "accepted", "rejected"].includes(e.outcome)
    ? e.outcome
    : "pending",
  statusOverride:
    e.statusOverride === null || e.statusOverride === undefined
      ? null
      : _asStr(e.statusOverride),
});

const canonConference = (c = {}) => ({
  id: c.id,
  title: _asStr(c.title || "Untitled Conference"),
  organizers: _asStr(c.organizers),
  titleExpanded: _asStr(c.titleExpanded),
  entries: (Array.isArray(c.entries) ? c.entries : []).map(canonEntry),
});

// One publication submission attempt (current fields or an archived history
// entry). Mirrors the backend's normalizePubAttempt so local and cloud copies
// canonicalize identically.
const canonPubAttempt = (a = {}) => ({
  venueType: a.venueType === "journal" ? "journal" : "conference",
  conferenceName: _asStr(a.conferenceName),
  conferenceLocation: _asStr(a.conferenceLocation),
  conferenceDateFrom: _emptyToNull(a.conferenceDateFrom),
  conferenceDateTo: _emptyToNull(a.conferenceDateTo),
  journalName: _asStr(a.journalName),
  publisher: _asStr(a.publisher),
  impactFactor: _asStr(a.impactFactor),
  quartile: ["Q1", "Q2", "Q3", "Q4"].includes(a.quartile) ? a.quartile : "",
  submittedDate: _emptyToNull(a.submittedDate),
  expectedDecisionDate: _emptyToNull(a.expectedDecisionDate),
  decisionDate: _emptyToNull(a.decisionDate),
  cameraReadyDate: _emptyToNull(a.cameraReadyDate),
  publishedDate: _emptyToNull(a.publishedDate),
  attemptNotes: _asStr(a.attemptNotes),
});

const canonStickyNote = (n = {}) => ({
  id: n.id,
  text: _asStr(n.text),
  color: _asStr(n.color || "yellow"),
  anchor: n.anchor ? String(n.anchor) : null,
  x: Number(n.x) || 0,
  y: Number(n.y) || 0,
  w: Number(n.w) || 220,
  h: Number(n.h) || 160,
  minimized: !!n.minimized,
  boardReturn: n.boardReturn || null,
  dueDate: n.dueDate || null,
  z: Number(n.z) || 0,
});
// Sync-diff view of a note: stacking order (`z`) is bumped on every click and
// is cosmetic, so it never triggers a cloud write on its own (it still travels
// with the record whenever something else about the note changes).
const canonStickyNoteSig = (n = {}) => {
  const { z, ...rest } = canonStickyNote(n);
  void z;
  return rest;
};
const canonPublication = (p = {}) => ({
  id: p.id,
  title: _asStr(p.title),
  shortName: _asStr(p.shortName),
  authors: _asStr(p.authors),
  status: _asStr(p.status || "drafting"),
  revisionNumber: _asStr(p.revisionNumber),
  comments: _asStr(p.comments),
  doiUrl: _asStr(p.doiUrl),
  preprintUrl: _asStr(p.preprintUrl),
  ...canonPubAttempt(p),
  history: (Array.isArray(p.history) ? p.history : []).map((h = {}) => ({
    ...canonPubAttempt(h),
    status: _asStr(h.status || "drafting"),
    revisionNumber: _asStr(h.revisionNumber),
    archivedAt: _asStr(h.archivedAt),
  })),
});

const canonSettings = (s = {}) => ({
  challengeName: _asStr(s.challengeName) || "My Challenge",
  numberOfDays: Number(s.numberOfDays) || 31,
  startDate: _asStr(s.startDate),
});

/**
 * Normalize a full data object into a comparison-only canonical form. See the
 * block comment above for why. Never mutates the input.
 */
function canonicalizeForCompare(data) {
  const p = data || {};
  return {
    settings: canonSettings(p.settings || {}),
    completedDays: Array.isArray(p.completedDays) ? p.completedDays : [],
    partialDays: Array.isArray(p.partialDays) ? p.partialDays : [],
    tabs: (Array.isArray(p.tabs) ? p.tabs : []).map(canonTab),
    subGoals: (Array.isArray(p.subGoals) ? p.subGoals : []).map(canonSubGoal),
    clipboardCards: (Array.isArray(p.clipboardCards)
      ? p.clipboardCards
      : []
    ).map(canonClip),
    quickLinkCards: (Array.isArray(p.quickLinkCards)
      ? p.quickLinkCards
      : []
    ).map(canonQuickLink),
    rememberItems: (Array.isArray(p.rememberItems) ? p.rememberItems : []).map(
      canonRemember,
    ),
    conferenceCards: (Array.isArray(p.conferenceCards)
      ? p.conferenceCards
      : []
    ).map(canonConference),
    publications: (Array.isArray(p.publications) ? p.publications : []).map(
      canonPublication,
    ),
  };
}

/**
 * Compare two data objects and find differences.
 * Both sides are canonicalized first (see canonicalizeForCompare) so only
 * genuine user-visible differences are reported — not auth fields, server
 * metadata, or defaulted-optional-field noise. Timestamps are read from the
 * ORIGINAL objects.
 */
export function compareData(localData, cloudData) {
  const L = canonicalizeForCompare(localData);
  const C = canonicalizeForCompare(cloudData);
  const differences = {
    hasDifferences: false,
    localNewer: false,
    cloudNewer: false,
    localTimestamp: localData?.lastModified || null,
    cloudTimestamp: cloudData?.lastModified || null,
    categories: {
      settings: { hasDiff: false, local: null, cloud: null },
      completedDays: {
        hasDiff: false,
        local: null,
        cloud: null,
        added: [],
        removed: [],
      },
      tabs: { hasDiff: false, local: null, cloud: null, changes: [] },
      subGoals: { hasDiff: false, local: null, cloud: null, changes: [] },
      clipboardCards: { hasDiff: false, local: null, cloud: null, changes: [] },
      quickLinkCards: { hasDiff: false, local: null, cloud: null, changes: [] },
      rememberItems: { hasDiff: false, local: null, cloud: null, changes: [] },
      conferenceCards: {
        hasDiff: false,
        local: null,
        cloud: null,
        changes: [],
      },
      publications: {
        hasDiff: false,
        local: null,
        cloud: null,
        changes: [],
      },
    },
  };
  
  // Determine which is newer based on timestamps
  if (localData?.lastModified && cloudData?.lastModified) {
    const localTime = new Date(localData.lastModified).getTime();
    const cloudTime = new Date(cloudData.lastModified).getTime();
    differences.localNewer = localTime > cloudTime;
    differences.cloudNewer = cloudTime > localTime;
  } else if (localData?.lastModified && !cloudData?.lastModified) {
    differences.localNewer = true;
  } else if (!localData?.lastModified && cloudData?.lastModified) {
    differences.cloudNewer = true;
  }
  
  // Compare Settings (auth fields already stripped by canonicalization)
  const localSettings = L.settings;
  const cloudSettings = C.settings;
  if (JSON.stringify(localSettings) !== JSON.stringify(cloudSettings)) {
    differences.hasDifferences = true;
    differences.categories.settings = {
      hasDiff: true,
      local: localSettings,
      cloud: cloudSettings,
      changes: findObjectDifferences(localSettings, cloudSettings),
    };
  }
  
  // Compare Completed Days
  const localDays = new Set(L.completedDays);
  const cloudDays = new Set(C.completedDays);
  const addedDays = [...localDays].filter((d) => !cloudDays.has(d));
  const removedDays = [...cloudDays].filter((d) => !localDays.has(d));
  if (addedDays.length > 0 || removedDays.length > 0) {
    differences.hasDifferences = true;
    differences.categories.completedDays = {
      hasDiff: true,
      local: Array.from(localDays),
      cloud: Array.from(cloudDays),
      added: addedDays,
      removed: removedDays,
    };
  }

  // Track partialDays diff so it triggers a sync, but resolve it together with completedDays
  const localPartialDays = new Set(L.partialDays);
  const cloudPartialDays = new Set(C.partialDays);
  const addedPartial = [...localPartialDays].filter(
    (d) => !cloudPartialDays.has(d),
  );
  const removedPartial = [...cloudPartialDays].filter(
    (d) => !localPartialDays.has(d),
  );
  if (addedPartial.length > 0 || removedPartial.length > 0) {
    differences.hasDifferences = true;
    // Mark completedDays category as having a diff so the conflict resolver
    // surfaces a single "day status" choice that covers both fields.
    differences.categories.completedDays.hasDiff = true;
    if (!differences.categories.completedDays.local) {
      differences.categories.completedDays.local = Array.from(localDays);
      differences.categories.completedDays.cloud = Array.from(cloudDays);
      differences.categories.completedDays.added =
        differences.categories.completedDays.added || [];
      differences.categories.completedDays.removed =
        differences.categories.completedDays.removed || [];
    }
  }
  
  // Compare Tabs & Goals
  const localTabs = L.tabs;
  const cloudTabs = C.tabs;
  const tabChanges = compareArrays(localTabs, cloudTabs, "id", "name");
  if (tabChanges.hasChanges) {
    differences.hasDifferences = true;
    differences.categories.tabs = {
      hasDiff: true,
      local: localTabs,
      cloud: cloudTabs,
      changes: tabChanges,
    };
  }
  
  // Compare Sub-Goals
  const localSubGoals = L.subGoals;
  const cloudSubGoals = C.subGoals;
  const subGoalChanges = compareArrays(
    localSubGoals,
    cloudSubGoals,
    "id",
    "text",
  );
  if (subGoalChanges.hasChanges) {
    differences.hasDifferences = true;
    differences.categories.subGoals = {
      hasDiff: true,
      local: localSubGoals,
      cloud: cloudSubGoals,
      changes: subGoalChanges,
    };
  }
  
  // Compare Clipboard Cards
  const localClipboard = L.clipboardCards;
  const cloudClipboard = C.clipboardCards;
  const clipboardChanges = compareArrays(
    localClipboard,
    cloudClipboard,
    "id",
    "title",
  );
  if (clipboardChanges.hasChanges) {
    differences.hasDifferences = true;
    differences.categories.clipboardCards = {
      hasDiff: true,
      local: localClipboard,
      cloud: cloudClipboard,
      changes: clipboardChanges,
    };
  }

  // Compare Quick Link Cards
  const localQuickLinks = L.quickLinkCards;
  const cloudQuickLinks = C.quickLinkCards;
  const quickLinkChanges = compareArrays(
    localQuickLinks,
    cloudQuickLinks,
    "id",
    "title",
  );
  if (quickLinkChanges.hasChanges) {
    differences.hasDifferences = true;
    differences.categories.quickLinkCards = {
      hasDiff: true,
      local: localQuickLinks,
      cloud: cloudQuickLinks,
      changes: quickLinkChanges,
    };
  }

  // Compare Remember Items
  const localRemember = L.rememberItems;
  const cloudRemember = C.rememberItems;
  const rememberChanges = compareArrays(
    localRemember,
    cloudRemember,
    "id",
    "text",
  );
  if (rememberChanges.hasChanges) {
    differences.hasDifferences = true;
    differences.categories.rememberItems = {
      hasDiff: true,
      local: localRemember,
      cloud: cloudRemember,
      changes: rememberChanges,
    };
  }

  // Compare Conference Cards
  const localConferences = L.conferenceCards;
  const cloudConferences = C.conferenceCards;
  const conferenceChanges = compareArrays(
    localConferences,
    cloudConferences,
    "id",
    "title",
  );
  if (conferenceChanges.hasChanges) {
    differences.hasDifferences = true;
    differences.categories.conferenceCards = {
      hasDiff: true,
      local: localConferences,
      cloud: cloudConferences,
      changes: conferenceChanges,
    };
  }

  // Compare Publications
  const localPublications = L.publications;
  const cloudPublications = C.publications;
  const publicationChanges = compareArrays(
    localPublications,
    cloudPublications,
    "id",
    "title",
  );
  if (publicationChanges.hasChanges) {
    differences.hasDifferences = true;
    differences.categories.publications = {
      hasDiff: true,
      local: localPublications,
      cloud: cloudPublications,
      changes: publicationChanges,
    };
  }
  
  return differences;
}

/**
 * Find differences between two objects
 */
function findObjectDifferences(obj1, obj2) {
  const changes = [];
  const allKeys = new Set([
    ...Object.keys(obj1 || {}),
    ...Object.keys(obj2 || {}),
  ]);
  
  for (const key of allKeys) {
    if (key === "userName" || key === "passkey") continue; // Skip auth fields
    const val1 = obj1?.[key];
    const val2 = obj2?.[key];
    if (JSON.stringify(val1) !== JSON.stringify(val2)) {
      changes.push({ key, local: val1, cloud: val2 });
    }
  }
  
  return changes;
}

/**
 * Compare two arrays of objects
 */
function compareArrays(arr1, arr2, idKey, displayKey) {
  const map1 = new Map((arr1 || []).map((item) => [item[idKey], item]));
  const map2 = new Map((arr2 || []).map((item) => [item[idKey], item]));
  
  const added = []; // In local but not cloud
  const removed = []; // In cloud but not local
  const modified = []; // In both but different
  
  // Check items in arr1
  for (const [id, item] of map1) {
    if (!map2.has(id)) {
      added.push(item);
    } else if (JSON.stringify(item) !== JSON.stringify(map2.get(id))) {
      modified.push({ local: item, cloud: map2.get(id) });
    }
  }
  
  // Check items only in arr2
  for (const [id, item] of map2) {
    if (!map1.has(id)) {
      removed.push(item);
    }
  }
  
  return {
    hasChanges: added.length > 0 || removed.length > 0 || modified.length > 0,
    added,
    removed,
    modified,
  };
}

/**
 * Merge data based on user selections
 * selections: { settings: 'local'|'cloud', completedDays: 'local'|'cloud', etc. }
 * itemSelections: { [itemId]: 'local'|'cloud' } for item-level choices
 */
export function mergeDataWithSelections(
  localData,
  cloudData,
  selections,
  itemSelections = {},
) {
  const merged = {
    lastModified: getCurrentTimestamp(),
  };
  
  // Merge settings
  if (selections.settings === "local") {
    merged.settings = localData?.settings || {};
  } else {
    merged.settings = cloudData?.settings || {};
  }
  
  // Merge completed days (and keep partialDays in sync with the same choice)
  if (selections.completedDays === "local") {
    merged.completedDays = localData?.completedDays || [];
    merged.partialDays = localData?.partialDays || [];
  } else if (selections.completedDays === "cloud") {
    merged.completedDays = cloudData?.completedDays || [];
    merged.partialDays = cloudData?.partialDays || [];
  } else {
    // Merge - union of both
    merged.completedDays = [
      ...new Set([
      ...(localData?.completedDays || []),
      ...(cloudData?.completedDays || []),
      ]),
    ];
    merged.partialDays = [
      ...new Set([
      ...(localData?.partialDays || []),
      ...(cloudData?.partialDays || []),
      ]),
    ];
    // A day cannot be both fully completed AND partial. Completed wins on union.
    const completedSet = new Set(merged.completedDays);
    merged.partialDays = merged.partialDays.filter((d) => !completedSet.has(d));
  }
  
  // Merge tabs with item-level selections
  merged.tabs = mergeArraysWithSelections(
    localData?.tabs || [],
    cloudData?.tabs || [],
    selections.tabs,
    itemSelections,
    "id",
  );
  
  // Merge sub-goals with item-level selections
  merged.subGoals = mergeArraysWithSelections(
    localData?.subGoals || [],
    cloudData?.subGoals || [],
    selections.subGoals,
    itemSelections,
    "id",
  );
  
  // Merge clipboard cards with item-level selections
  merged.clipboardCards = mergeArraysWithSelections(
    localData?.clipboardCards || [],
    cloudData?.clipboardCards || [],
    selections.clipboardCards,
    itemSelections,
    "id",
  );

  // Merge quick link cards with item-level selections
  merged.quickLinkCards = mergeArraysWithSelections(
    localData?.quickLinkCards || [],
    cloudData?.quickLinkCards || [],
    selections.quickLinkCards,
    itemSelections,
    "id",
  );

  // Merge remember items with item-level selections
  merged.rememberItems = mergeArraysWithSelections(
    localData?.rememberItems || [],
    cloudData?.rememberItems || [],
    selections.rememberItems,
    itemSelections,
    "id",
  );

  // Merge conference cards with item-level selections. CRITICAL: this category
  // must always be present in the merged payload — omitting it previously caused
  // the full-replace save to wipe all conferences.
  merged.conferenceCards = mergeArraysWithSelections(
    localData?.conferenceCards || [],
    cloudData?.conferenceCards || [],
    selections.conferenceCards,
    itemSelections,
    "id",
  );

  // Publications must also always be present in the merged payload so a
  // full-replace save can never wipe them (same guard as conferenceCards).
  merged.publications = mergeArraysWithSelections(
    localData?.publications || [],
    cloudData?.publications || [],
    selections.publications,
    itemSelections,
    "id",
  );

  // Carry through the conference-related display settings (and panelOrder).
  // These are not surfaced as their own conflict category, so we follow the
  // settings choice and fall back across both sources. `??` (not `||`) so a
  // legitimate `false` / empty value is preserved. Without this, resolving any
  // conflict would reset showConferences to undefined and hide the panel.
  const preferCloudSettings = selections.settings === "cloud";
  const pickScalar = (key) => {
    const primary = preferCloudSettings ? cloudData?.[key] : localData?.[key];
    const secondary = preferCloudSettings ? localData?.[key] : cloudData?.[key];
    return primary ?? secondary;
  };
  merged.showConferences = pickScalar("showConferences") ?? false;
  merged.conferenceSortMode = pickScalar("conferenceSortMode");
  merged.showPublications = pickScalar("showPublications") ?? false;
  merged.publicationSortMode = pickScalar("publicationSortMode");
  merged.showRememberInReminders =
    pickScalar("showRememberInReminders") ?? true;
  merged.panelOrder = pickScalar("panelOrder");
  
  // Preserve activeTabId
  merged.activeTabId =
    selections.tabs === "local"
    ? localData?.activeTabId 
    : cloudData?.activeTabId;
  
  return merged;
}

/**
 * Merge arrays with item-level selections
 */
function mergeArraysWithSelections(
  localArr,
  cloudArr,
  categorySelection,
  itemSelections,
  idKey,
) {
  if (categorySelection === "local") return localArr;
  if (categorySelection === "cloud") return cloudArr;
  
  // For item-level merging
  const localMap = new Map(localArr.map((item) => [item[idKey], item]));
  const cloudMap = new Map(cloudArr.map((item) => [item[idKey], item]));
  const allIds = new Set([...localMap.keys(), ...cloudMap.keys()]);
  
  const merged = [];
  for (const id of allIds) {
    const selection = itemSelections[id];
    if (selection === "local" && localMap.has(id)) {
      merged.push(localMap.get(id));
    } else if (selection === "cloud" && cloudMap.has(id)) {
      merged.push(cloudMap.get(id));
    } else if (localMap.has(id) && !cloudMap.has(id)) {
      merged.push(localMap.get(id));
    } else if (cloudMap.has(id) && !localMap.has(id)) {
      merged.push(cloudMap.get(id));
    } else if (localMap.has(id)) {
      // Default to local if no selection
      merged.push(localMap.get(id));
    }
  }
  
  return merged;
}

/**
 * Kept for backwards compatibility. Convex manages its own schema, so there is
 * nothing to initialize on the client.
 */
export async function initializeSheets() {
  return { success: true, message: "No initialization needed (Convex)" };
}

/**
 * Fetch all data from Convex for the active user.
 */
export async function fetchAllData() {
  const convex = getConvexClient();
  if (!convex) {
    throw new Error("Convex is not configured (REACT_APP_CONVEX_URL missing)");
  }
  const userName = getActiveUserName();
  if (!userName) {
    throw new Error("No authenticated user for cloud fetch");
  }
  try {
    return await convex.query(convexRefs.getAll, {
      token: getActiveToken(),
      userName,
    });
  } catch (error) {
    console.error("Error fetching data from Convex:", error);
    throw error;
  }
}

/**
 * Save all data to Convex (atomic full replace for the active user).
 */
export async function saveAllData(data) {
  const convex = getConvexClient();
  if (!convex) {
    throw new Error("Convex is not configured (REACT_APP_CONVEX_URL missing)");
  }
  const userName = getActiveUserName(
    data && data.settings && data.settings.userName,
  );
  if (!userName) {
    throw new Error("No authenticated user for cloud save");
  }
  try {
    return await convex.mutation(convexRefs.saveAll, {
      token: getActiveToken(),
      userName,
      payload: data,
    });
  } catch (error) {
    console.error("Error saving data to Convex:", error);
    throw error;
  }
}

/**
 * List the active user's saved versions (snapshots), newest first. Returns
 * metadata only ({ id, takenAt, reason, summary }).
 */
export async function fetchSnapshots() {
  const convex = getConvexClient();
  if (!convex) {
    throw new Error("Convex is not configured (REACT_APP_CONVEX_URL missing)");
  }
  const userName = getActiveUserName();
  if (!userName) {
    throw new Error("No authenticated user for snapshot list");
  }
  return await convex.query(convexRefs.listSnapshots, {
    token: getActiveToken(),
    userName,
  });
}

/**
 * Fetch a single snapshot's full state for the "view version" preview. Returns
 * the metadata plus `data` parsed into a state object (null if unreadable).
 */
export async function fetchSnapshot(snapshotId) {
  const convex = getConvexClient();
  if (!convex) {
    throw new Error("Convex is not configured (REACT_APP_CONVEX_URL missing)");
  }
  const userName = getActiveUserName();
  if (!userName) {
    throw new Error("No authenticated user for snapshot view");
  }
  const snap = await convex.query(convexRefs.getSnapshot, {
    token: getActiveToken(),
    userName,
    snapshotId,
  });
  let data = null;
  try {
    data = snap && snap.data ? JSON.parse(snap.data) : null;
  } catch (e) {
    data = null;
  }
  return { ...snap, data };
}

/**
 * Restore the active user's data from a previous snapshot. The current state is
 * snapshotted first on the server, so the restore is itself undoable.
 */
export async function restoreSnapshot(snapshotId) {
  const convex = getConvexClient();
  if (!convex) {
    throw new Error("Convex is not configured (REACT_APP_CONVEX_URL missing)");
  }
  const userName = getActiveUserName();
  if (!userName) {
    throw new Error("No authenticated user for snapshot restore");
  }
  return await convex.mutation(convexRefs.restoreSnapshot, {
    token: getActiveToken(),
    userName,
    snapshotId,
  });
}

/**
 * Restore ONE section of a snapshot ("tabs", "goals", "subGoals",
 * "clipboardCards", "quickLinkCards", "rememberItems", "conferenceCards",
 * "publications", "stickyNotes", "days"). Additive: the version's items come
 * back, items added since are kept. Resolves { section, restored, restoredFrom }.
 */
export async function restoreSnapshotSection(snapshotId, section) {
  const convex = getConvexClient();
  if (!convex) {
    throw new Error("Convex is not configured (REACT_APP_CONVEX_URL missing)");
  }
  return await convex.mutation(convexRefs.restoreSnapshotSection, {
    token: getActiveToken(),
    snapshotId,
    section,
  });
}

/** Save a version of the cloud state right now. Resolves { saved } (false = identical to newest). */
export async function saveSnapshotNow() {
  const convex = getConvexClient();
  if (!convex) throw new Error("Convex is not configured");
  return await convex.mutation(convexRefs.saveSnapshotNow, {
    token: getActiveToken(),
  });
}

/** Delete every saved version. */
export async function clearAllSnapshots() {
  const convex = getConvexClient();
  if (!convex) throw new Error("Convex is not configured");
  return await convex.mutation(convexRefs.clearSnapshots, {
    token: getActiveToken(),
  });
}

/** Version-history schedule: { everyDays, hour, tz, tzOffsetMinutes, max, lastAutoDay, exists }. */
export async function fetchSnapshotPrefs() {
  const convex = getConvexClient();
  if (!convex) throw new Error("Convex is not configured");
  return await convex.query(convexRefs.getSnapshotPrefs, {
    token: getActiveToken(),
  });
}

/** Browser time zone (IANA name + current UTC offset) for the snapshot schedule. */
export function localTimeZone() {
  let tz = "";
  try {
    tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch (e) {
    tz = "";
  }
  return { tz, tzOffsetMinutes: -new Date().getTimezoneOffset() };
}

export async function saveSnapshotPrefs({ everyDays, hour, max }) {
  const convex = getConvexClient();
  if (!convex) throw new Error("Convex is not configured");
  const { tz, tzOffsetMinutes } = localTimeZone();
  return await convex.mutation(convexRefs.setSnapshotPrefs, {
    token: getActiveToken(),
    ...(everyDays !== undefined ? { everyDays: Number(everyDays) } : {}),
    ...(hour !== undefined ? { hour: Number(hour) } : {}),
    ...(max !== undefined ? { max: Number(max) } : {}),
    ...(tz ? { tz } : {}),
    tzOffsetMinutes,
  });
}

// ---------- passkey management ----------
/** { custom, updatedAt, recoveryRemaining } for the signed-in account. */
export async function fetchPasskeyStatus() {
  const convex = getConvexClient();
  if (!convex) throw new Error("Convex is not configured");
  return await withTimeout(
    convex.query(convexRefs.passkeyStatus, { token: getActiveToken() }),
    15000,
  );
}

/** Change the passkey; resolves { recoveryCodes: string[] } (shown once). */
export async function changePasskey({ current, next, signOutOthers }) {
  const convex = getConvexClient();
  if (!convex) throw new Error("Convex is not configured");
  return await withTimeout(
    convex.mutation(convexRefs.changePasskey, {
      token: getActiveToken(),
      current,
      next,
      signOutOthers: !!signOutOthers,
    }),
    20000,
  );
}

/** Replace the recovery codes; resolves { recoveryCodes }. */
export async function regenerateRecoveryCodes(current) {
  const convex = getConvexClient();
  if (!convex) throw new Error("Convex is not configured");
  return await withTimeout(
    convex.mutation(convexRefs.regenerateRecoveryCodes, {
      token: getActiveToken(),
      current,
    }),
    20000,
  );
}

/**
 * "Forgot passkey": a one-time recovery code sets a new passkey and signs this
 * device in (every other session is revoked). Resolves { token, userName }.
 */
export async function resetPasskeyWithRecovery({ userName, code, next }) {
  const convex = getConvexClient();
  if (!convex) throw new Error("Convex is not configured");
  return await withTimeout(
    convex.mutation(convexRefs.resetPasskeyWithRecovery, {
      userName,
      code,
      next,
      deviceName: describeDevice(),
      platform: "web",
    }),
    20000,
  );
}

/**
 * Debounce function to limit API calls
 */
export function debounce(func, wait) {
  let timeout;
  const executedFunction = function (...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
  executedFunction.flush = () => {
    if (timeout) {
      clearTimeout(timeout);
      timeout = null;
      func();
    }
  };
  executedFunction.cancel = () => {
    clearTimeout(timeout);
    timeout = null;
  };
  return executedFunction;
}

/**
 * Check if we're in development mode
 */
export function isDevelopment() {
  return process.env.NODE_ENV === "development";
}

/**
 * Migrate data from localStorage to Google Sheets
 */
export async function migrateFromLocalStorage() {
  try {
    const STORAGE_KEY = "xDayChallengerData";
    const savedData = localStorage.getItem(STORAGE_KEY);
    
    if (!savedData) {
      return { success: false, message: "No localStorage data found" };
    }

    const data = JSON.parse(savedData);
    
    // Initialize sheets first
    await initializeSheets();
    
    // Save the data
    await saveAllData({
      settings: data.settings || {
        challengeName: "My Challenge",
        numberOfDays: 31,
        startDate: new Date().toISOString().split("T")[0],
        userName: "",
      },
      completedDays: data.completedDays || [],
      partialDays: data.partialDays || [],
      tabs: data.tabs || [],
      subGoals: data.subGoals || [],
      clipboardCards: data.clipboardCards || [],
      quickLinkCards: data.quickLinkCards || [],
      rememberItems: data.rememberItems || [],
      conferenceCards: data.conferenceCards || [],
      showConferences: data.showConferences,
      conferenceSortMode: data.conferenceSortMode,
      publications: data.publications || [],
      showPublications: data.showPublications,
      publicationSortMode: data.publicationSortMode,
      showRememberInReminders: data.showRememberInReminders,
      panelOrder: data.panelOrder,
    });

    return { success: true, message: "Data migrated successfully" };
  } catch (error) {
    console.error("Error migrating data:", error);
    throw error;
  }
}

/**
 * Cleanup duplicate entries. With Convex, every save is an atomic full-replace
 * that already deduplicates by ID, so duplicates cannot accumulate. Kept for
 * compatibility with the Settings UI.
 */
export async function cleanupDuplicates() {
  return {
    success: true,
    message:
      "No duplicates to clean — Convex storage stays consistent automatically.",
  };
}

// ============================================================================
// Record-level two-way sync (outbox + delta pull). See data.ts applyChanges /
// getChangesSince. The frontend tracks local edits in a persisted OUTBOX (keyed
// by `table:id`), each entry stamped at edit-time, and flushes them as deltas.
// It pulls remote deltas via a server-clock CURSOR and merges them by
// Last-Writer-Wins. No full-replace anywhere → no cross-device clobber.
// ============================================================================

const DEVICE_KEY = "xDayDeviceId";
const CURSOR_KEY = "xDaySyncCursor";
const OUTBOX_KEY = "xDaySyncOutbox";
const BOOTSTRAP_KEY = "xDaySyncBootstrapped";
const OUTBOX_ARCHIVE_KEY = "xDaySyncOutboxArchive";
const SYNC_STATE_VERSION_KEY = "xDaySyncStateVersion";
// Outbox entries the server has definitively rejected (e.g. a malformed record
// from an old build). They are moved here — never deleted — so one bad entry
// can't block the rest of the outbox from syncing forever.
const QUARANTINE_KEY = "xDaySyncQuarantine";

// Bump this whenever a released change makes the previously-persisted sync
// state (outbox / cursor / bootstrap flag) untrustworthy. On the first load of
// a build with a newer version than the device has stored, migrateSyncStateIfNeeded
// archives and clears that state so the next load reconciles cleanly from the
// cloud. v2 retires the old "bootstrap-before-pull" + now-stamped outbox model.
const SYNC_STATE_VERSION = 2;

// A timestamp older than any real edit. Used to stamp legacy local-only records
// (created before sync existed, with no updatedAt/createdAt) so that, in the
// unlikely event the cloud already has the same record, the cloud always wins.
const SYNC_EPOCH = "1970-01-01T00:00:00.000Z";

// Per-table timeout (ms) for the two cloud sync calls. Without this a stalled
// request would leave the UI stuck on "Syncing" forever and disable Sync Now.
const SYNC_CALL_TIMEOUT_MS = 20000;

function safeUuid() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID)
      return crypto.randomUUID();
  } catch (e) {
    /* ignore */
  }
  return (
    "id-" +
    Date.now().toString(36) +
    "-" +
    Math.random().toString(36).slice(2, 10)
  );
}

/** Stable per-device id (LWW tiebreak), created once and persisted. */
export function getDeviceId() {
  try {
    let id = localStorage.getItem(DEVICE_KEY);
    if (!id) {
      id = safeUuid();
      localStorage.setItem(DEVICE_KEY, id);
    }
    return id;
  } catch (e) {
    return "device";
  }
}

/** A fresh collision-proof id for a new record. */
export function newRecordId() {
  return safeUuid();
}

export function getSyncCursor() {
  try {
    return localStorage.getItem(CURSOR_KEY) || null;
  } catch (e) {
    return null;
  }
}
export function setSyncCursor(ts) {
  try {
    if (ts) localStorage.setItem(CURSOR_KEY, String(ts));
  } catch (e) {
    /* ignore */
  }
}

export function readOutbox() {
  try {
    const raw = localStorage.getItem(OUTBOX_KEY);
    const obj = raw ? JSON.parse(raw) : {};
    return obj && typeof obj === "object" ? obj : {};
  } catch (e) {
    return {};
  }
}
export function writeOutbox(obj) {
  try {
    localStorage.setItem(OUTBOX_KEY, JSON.stringify(obj || {}));
  } catch (e) {
    /* ignore */
  }
}
export function outboxCount() {
  return Object.keys(readOutbox()).length;
}

export function isBootstrapped() {
  try {
    return localStorage.getItem(BOOTSTRAP_KEY) === "true";
  } catch (e) {
    return false;
  }
}
export function setBootstrapped() {
  try {
    localStorage.setItem(BOOTSTRAP_KEY, "true");
  } catch (e) {
    /* ignore */
  }
}

/** Force the next pull to be a full pull (e.g. after a snapshot restore). */
export function clearSyncCursor() {
  try {
    localStorage.removeItem(CURSOR_KEY);
  } catch (e) {
    /* ignore */
  }
}

/** Reset all per-session sync state (used on logout). Keeps deviceId. */
export function clearSyncState() {
  try {
    localStorage.removeItem(CURSOR_KEY);
    localStorage.removeItem(OUTBOX_KEY);
    localStorage.removeItem(BOOTSTRAP_KEY);
  } catch (e) {
    /* ignore */
  }
}

/**
 * One-time, version-gated migration of the persisted sync state.
 *
 * Earlier builds bootstrapped pre-existing local data into the outbox BEFORE
 * pulling, and stamped many records with the wall-clock `now`, which let stale
 * local data both suppress and overwrite newer cloud data. That poison lives in
 * each device's localStorage, so a pure code fix isn't enough — every device
 * must shed the bad outbox/cursor/bootstrap state once on upgrade.
 *
 * This runs at most once per device per version bump. To avoid silently
 * dropping any genuinely un-synced edits that happened to be pending at the
 * moment of upgrade, the old outbox is ARCHIVED (not destroyed) and the number
 * of archived entries is returned so the UI can surface a visible notice.
 *
 * @returns {{ migrated: boolean, archivedCount: number }}
 */
export function migrateSyncStateIfNeeded() {
  try {
    const stored = Number(localStorage.getItem(SYNC_STATE_VERSION_KEY) || 0);
    if (stored >= SYNC_STATE_VERSION) {
      return { migrated: false, archivedCount: 0 };
    }
    const outbox = readOutbox();
    const archivedCount = Object.keys(outbox).length;
    if (archivedCount > 0) {
      try {
        localStorage.setItem(
          OUTBOX_ARCHIVE_KEY,
          JSON.stringify({
            archivedAt: getCurrentTimestamp(),
            version: stored,
            outbox,
          }),
        );
      } catch (e) {
        /* archiving is best-effort */
      }
    }
    // Discard the untrustworthy state. The next load does a full pull (cloud
    // wins) and re-pushes any truly local-only records, so nothing real is lost.
    localStorage.removeItem(CURSOR_KEY);
    localStorage.removeItem(OUTBOX_KEY);
    localStorage.removeItem(BOOTSTRAP_KEY);
    localStorage.setItem(SYNC_STATE_VERSION_KEY, String(SYNC_STATE_VERSION));
    return { migrated: true, archivedCount };
  } catch (e) {
    return { migrated: false, archivedCount: 0 };
  }
}

/** LWW comparison (mirrors data.ts versionNewer): is A newer than B? */
export function isNewer(aUpdatedAt, aDeviceId, bUpdatedAt, bDeviceId) {
  const a = Date.parse(aUpdatedAt || "") || 0;
  const b = Date.parse(bUpdatedAt || "") || 0;
  if (a !== b) return a > b;
  return String(aDeviceId || "") > String(bDeviceId || "");
}

// ---- snapshot shaping (compare full-state objects) ----
const flatTabs = (state) =>
  (state.tabs || []).map((t, i) => ({
    id: t.id,
    name: t.name,
    order: i,
    createdAt: t.createdAt,
    updatedAt: t.updatedAt,
  }));
const flatGoals = (state) =>
  (state.tabs || []).flatMap((t) =>
    (t.goals || []).map((g) => ({ ...g, tabId: t.id })),
  );
const dayStatusMap = (state) => {
  const m = new Map();
  (state.completedDays || []).forEach((d) => m.set(d, "complete"));
  (state.partialDays || []).forEach((d) => {
    if (!m.has(d)) m.set(d, "partial");
  });
  return m;
};
const settingsPayload = (state) => ({
  challengeName: state.settings?.challengeName ?? "My Challenge",
  numberOfDays: Number(state.settings?.numberOfDays) || 31,
  startDate: state.settings?.startDate ?? "",
  panelOrder: Array.isArray(state.panelOrder) ? state.panelOrder : undefined,
  showConferences: !!state.showConferences,
  conferenceSortMode: state.conferenceSortMode || undefined,
  showPublications: !!state.showPublications,
  publicationSortMode: state.publicationSortMode || undefined,
  showRememberInReminders:
    typeof state.showRememberInReminders === "boolean"
      ? state.showRememberInReminders
      : true,
  // Appearance + UI prefs (cloud-persisted so they follow the account).
  theme: typeof state.theme === "string" ? state.theme : undefined,
  skin: typeof state.skin === "string" ? state.skin : undefined,
  ambientEffects:
    typeof state.ambientEffects === "boolean"
      ? state.ambientEffects
      : undefined,
  calendarCollapsed:
    typeof state.calendarCollapsed === "boolean"
      ? state.calendarCollapsed
      : undefined,
  streakReminderHour:
    typeof state.streakReminderHour === "number"
      ? state.streakReminderHour
      : undefined,
  railSide: typeof state.railSide === "string" ? state.railSide : undefined,
  collapsedPanels: Array.isArray(state.collapsedPanels)
    ? state.collapsedPanels
    : undefined,
  notesGathered:
    typeof state.notesGathered === "boolean" ? state.notesGathered : undefined,
  notesSavedLayout:
    typeof state.notesSavedLayout === "string"
      ? state.notesSavedLayout
      : undefined,
});
const settingsSig = (state) =>
  JSON.stringify({
    ...canonSettings(state.settings || {}),
    panelOrder: Array.isArray(state.panelOrder) ? state.panelOrder : null,
    showConferences: !!state.showConferences,
    conferenceSortMode: state.conferenceSortMode || null,
    showPublications: !!state.showPublications,
    publicationSortMode: state.publicationSortMode || null,
    showRememberInReminders:
      typeof state.showRememberInReminders === "boolean"
        ? state.showRememberInReminders
        : true,
    theme: state.theme || null,
    skin: state.skin || null,
    ambientEffects:
      typeof state.ambientEffects === "boolean" ? state.ambientEffects : null,
    calendarCollapsed:
      typeof state.calendarCollapsed === "boolean"
        ? state.calendarCollapsed
        : null,
    streakReminderHour:
      typeof state.streakReminderHour === "number"
        ? state.streakReminderHour
        : null,
    railSide: state.railSide || null,
    collapsedPanels: Array.isArray(state.collapsedPanels)
      ? state.collapsedPanels.slice().sort()
      : null,
    notesGathered:
      typeof state.notesGathered === "boolean" ? state.notesGathered : null,
    notesSavedLayout:
      typeof state.notesSavedLayout === "string"
        ? state.notesSavedLayout
        : null,
  });
const canonGoalWithTab = (g) => ({ ...canonGoal(g), tabId: String(g.tabId) });
const canonTabShallow = (t) => ({ name: String(t.name || ""), order: t.order });

function diffRecords(table, prevArr, currArr, canonFn, deviceId, now, entries) {
  const prevMap = new Map((prevArr || []).map((r) => [String(r.id), r]));
  const currMap = new Map((currArr || []).map((r) => [String(r.id), r]));
  for (const [id, rec] of currMap) {
    const p = prevMap.get(id);
    if (!p || JSON.stringify(canonFn(p)) !== JSON.stringify(canonFn(rec))) {
      entries.push({
        key: `${table}:${id}`,
        table,
        id: rec.id,
        record: rec,
        updatedAt: now,
        deviceId,
      });
    }
  }
  for (const [id, rec] of prevMap) {
    if (!currMap.has(id)) {
      // Deletes also carry their relational ids (goals.tabId / subGoals.goalId)
      // so the backend always receives every schema-required field.
      entries.push({
        key: `${table}:${id}`,
        table,
        id: rec.id,
        deleted: true,
        updatedAt: now,
        deviceId,
        ...(rec.tabId !== undefined ? { tabId: rec.tabId } : {}),
        ...(rec.goalId !== undefined ? { goalId: rec.goalId } : {}),
      });
    }
  }
}

/**
 * Diff two full-state snapshots into outbox entries (upserts + tombstones).
 * `now` is the edit-time stamp. A record is only tombstoned if it was in `prev`
 * and is gone from `curr` (a real local delete) — a wrong/empty prev can never
 * mass-delete.
 */
export function diffStates(prev, curr, deviceId, now) {
  const entries = [];
  if (settingsSig(prev) !== settingsSig(curr)) {
    entries.push({
      key: "settings",
      table: "settings",
      settings: settingsPayload(curr),
      updatedAt: now,
      deviceId,
    });
  }
  const pm = dayStatusMap(prev);
  const cm = dayStatusMap(curr);
  for (const [date, status] of cm) {
    if (pm.get(date) !== status) {
      entries.push({
        key: `completedDays:${date}`,
        table: "completedDays",
        date,
        status,
        updatedAt: now,
        deviceId,
      });
    }
  }
  for (const [date] of pm) {
    if (!cm.has(date)) {
      entries.push({
        key: `completedDays:${date}`,
        table: "completedDays",
        date,
        deleted: true,
        updatedAt: now,
        deviceId,
      });
    }
  }
  diffRecords(
    "tabs",
    flatTabs(prev),
    flatTabs(curr),
    canonTabShallow,
    deviceId,
    now,
    entries,
  );
  diffRecords(
    "goals",
    flatGoals(prev),
    flatGoals(curr),
    canonGoalWithTab,
    deviceId,
    now,
    entries,
  );
  diffRecords(
    "subGoals",
    prev.subGoals,
    curr.subGoals,
    canonSubGoal,
    deviceId,
    now,
    entries,
  );
  diffRecords(
    "clipboardCards",
    prev.clipboardCards,
    curr.clipboardCards,
    canonClip,
    deviceId,
    now,
    entries,
  );
  diffRecords(
    "quickLinkCards",
    prev.quickLinkCards,
    curr.quickLinkCards,
    canonQuickLink,
    deviceId,
    now,
    entries,
  );
  diffRecords(
    "rememberItems",
    prev.rememberItems,
    curr.rememberItems,
    canonRemember,
    deviceId,
    now,
    entries,
  );
  diffRecords(
    "conferenceCards",
    prev.conferenceCards,
    curr.conferenceCards,
    canonConference,
    deviceId,
    now,
    entries,
  );
  diffRecords(
    "publications",
    prev.publications,
    curr.publications,
    canonPublication,
    deviceId,
    now,
    entries,
  );
  diffRecords(
    "stickyNotes",
    prev.stickyNotes,
    curr.stickyNotes,
    canonStickyNoteSig,
    deviceId,
    now,
    entries,
  );
  return entries;
}

// Settings fields that are real data (vs. appearance / layout preferences).
// A change that touches ONLY preferences is coalesced into a slow flush — it
// still rides along with the next real edit — so clicking panels open/closed
// or switching the theme no longer costs a cloud write each time.
const settingsDataSig = (state) =>
  JSON.stringify({
    ...canonSettings(state.settings || {}),
    showConferences: !!state.showConferences,
    showPublications: !!state.showPublications,
    showRememberInReminders:
      typeof state.showRememberInReminders === "boolean"
        ? state.showRememberInReminders
        : true,
  });

/** True when the diff between two snapshots is UI preferences only. */
export function isUiOnlyChange(prev, curr, entries) {
  if (!entries.length) return false;
  if (!entries.every((e) => e.table === "settings")) return false;
  return settingsDataSig(prev) === settingsDataSig(curr);
}

/**
 * One-time bootstrap: enqueue ALL local records as upserts so pre-existing local
 * data (created before sync existed) reaches the cloud. Each record keeps its own
 * timestamp so LWW correctly defers to anything newer already on the server.
 */
export function buildBootstrapEntries(state, deviceId) {
  const now = getCurrentTimestamp();
  const stamp = (r) => r.updatedAt || r.createdAt || now;
  const entries = [];
  entries.push({
    key: "settings",
    table: "settings",
    settings: settingsPayload(state),
    updatedAt: state.lastModified || now,
    deviceId,
  });
  for (const [date, status] of dayStatusMap(state)) {
    entries.push({
      key: `completedDays:${date}`,
      table: "completedDays",
      date,
      status,
      updatedAt: now,
      deviceId,
    });
  }
  const pushAll = (table, arr, stampFn) => {
    (arr || []).forEach((r) => {
      entries.push({
        key: `${table}:${String(r.id)}`,
        table,
        id: r.id,
        record: r,
        updatedAt: stampFn(r),
        deviceId,
      });
    });
  };
  pushAll("tabs", flatTabs(state), stamp);
  pushAll("goals", flatGoals(state), stamp);
  pushAll("subGoals", state.subGoals, stamp);
  pushAll("clipboardCards", state.clipboardCards, stamp);
  pushAll("quickLinkCards", state.quickLinkCards, stamp);
  pushAll("rememberItems", state.rememberItems, stamp);
  pushAll("conferenceCards", state.conferenceCards, stamp);
  pushAll("publications", state.publications, stamp);
  pushAll("stickyNotes", state.stickyNotes, stamp);
  return entries;
}

/**
 * Reconcile local-only data UP to the cloud, safely. Given the local full state
 * and the result of a full pull (getChangesSince with no cursor), this enqueues
 * outbox entries ONLY for records that exist locally but are entirely unknown to
 * the cloud — never for records the cloud already has (deleted or not). Because
 * a cloud-present record is never re-pushed from here, stale local copies can
 * never clobber the cloud; the full-pull merge is the single source of truth for
 * shared records, and this only fills genuine gaps (e.g. data created offline or
 * in a pre-sync version that never reached the cloud).
 *
 * Records keep their own updatedAt/createdAt where available, falling back to
 * SYNC_EPOCH so that even if the cloud later turns out to have the same id, the
 * cloud wins the Last-Writer-Wins comparison.
 */
export function buildLocalOnlyEntries(localData, cloudRes, deviceId) {
  const state = localData || {};
  const res = cloudRes || {};
  const entries = [];
  const stamp = (r) => r.updatedAt || r.createdAt || SYNC_EPOCH;

  // Settings is a single row: only push local settings if the cloud has none.
  if (!res.settings) {
    entries.push({
      key: "settings",
      table: "settings",
      settings: settingsPayload(state),
      updatedAt: state.lastModified || SYNC_EPOCH,
      deviceId,
    });
  }

  // Day status: push only dates the cloud has never heard of (cloud rows include
  // tombstones, so a date deleted elsewhere counts as "known" and is left alone).
  const cloudDays = new Set((res.completedDays || []).map((d) => d.date));
  for (const [date, status] of dayStatusMap(state)) {
    if (!cloudDays.has(date)) {
      entries.push({
        key: `completedDays:${date}`,
        table: "completedDays",
        date,
        status,
        updatedAt: SYNC_EPOCH,
        deviceId,
      });
    }
  }

  const pushMissing = (table, localArr, cloudArr) => {
    const known = new Set((cloudArr || []).map((r) => String(r.id)));
    (localArr || []).forEach((r) => {
      if (r && !known.has(String(r.id))) {
        entries.push({
          key: `${table}:${String(r.id)}`,
          table,
          id: r.id,
          record: r,
          updatedAt: stamp(r),
          deviceId,
        });
      }
    });
  };

  pushMissing("tabs", flatTabs(state), res.tabs);
  pushMissing("goals", flatGoals(state), res.goals);
  pushMissing("subGoals", state.subGoals, res.subGoals);
  pushMissing("clipboardCards", state.clipboardCards, res.clipboardCards);
  pushMissing("quickLinkCards", state.quickLinkCards, res.quickLinkCards);
  pushMissing("rememberItems", state.rememberItems, res.rememberItems);
  pushMissing("conferenceCards", state.conferenceCards, res.conferenceCards);
  pushMissing("publications", state.publications, res.publications);
  pushMissing("stickyNotes", state.stickyNotes, res.stickyNotes);
  return entries;
}

/** Merge new entries into the persisted outbox (keyed by entry.key; latest wins). */
export function enqueueChanges(entries) {
  if (!entries || entries.length === 0) return;
  const obj = readOutbox();
  for (const e of entries) obj[e.key] = e;
  writeOutbox(obj);
}

function outboxToChanges(obj) {
  const changes = {};
  for (const entry of Object.values(obj)) {
    if (entry.table === "settings") {
      changes.settings = { ...entry.settings, lastModified: entry.updatedAt };
    } else if (entry.table === "completedDays") {
      (changes.completedDays = changes.completedDays || []).push({
        date: entry.date,
        status: entry.status,
        updatedAt: entry.updatedAt,
        deviceId: entry.deviceId,
        ...(entry.deleted ? { deleted: true } : {}),
      });
    } else {
      const arr = (changes[entry.table] = changes[entry.table] || []);
      if (entry.deleted) {
        arr.push({
          id: entry.id,
          deleted: true,
          updatedAt: entry.updatedAt,
          deviceId: entry.deviceId,
          ...(entry.tabId !== undefined ? { tabId: entry.tabId } : {}),
          ...(entry.goalId !== undefined ? { goalId: entry.goalId } : {}),
        });
      } else {
        arr.push({
          ...entry.record,
          id: entry.id,
          updatedAt: entry.updatedAt,
          deviceId: entry.deviceId,
        });
      }
    }
  }
  return changes;
}

/**
 * Was this sync failure a transient transport problem (offline, flaky network,
 * timeout)? Those must be RETRIED with the outbox intact. Anything else came
 * back from the server itself (validation/application error) and will fail the
 * same way every time — retrying can never succeed.
 */
function isTransientSyncError(e) {
  const msg = String((e && e.message) || e || "").toLowerCase();
  return (
    msg.includes("timeout") ||
    msg.includes("fetch") || // Chrome/Firefox: "Failed to fetch"
    msg.includes("load failed") || // Safari's fetch failure message
    msg.includes("network") ||
    msg.includes("connect")
  );
}

/** Move rejected outbox entries to persistent quarantine (never deleted). */
function appendQuarantine(entries) {
  try {
    const raw = localStorage.getItem(QUARANTINE_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    arr.push(...entries);
    localStorage.setItem(QUARANTINE_KEY, JSON.stringify(arr));
  } catch (e) {
    /* best-effort */
  }
}

export function quarantineCount() {
  try {
    const raw = localStorage.getItem(QUARANTINE_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.length : 0;
  } catch (e) {
    return 0;
  }
}

/**
 * Flush the outbox to the cloud as a single applyChanges delta. On success,
 * prunes only the entries that weren't re-edited mid-flight (so concurrent
 * edits are never lost). Throws on transient network errors (caller keeps the
 * outbox and retries later).
 *
 * If the SERVER rejects the batch (a permanent error — retrying is futile),
 * each entry is retried individually so one bad entry can't block everything;
 * entries that are individually rejected are moved to quarantine (kept in
 * localStorage, never destroyed) and reported via `quarantined` so the UI can
 * tell the user. This makes the outbox self-healing instead of permanently
 * wedged on a poison entry.
 */
export async function flushOutbox() {
  const convex = getConvexClient();
  if (!convex)
    return { ok: false, reason: "not_configured", remaining: outboxCount() };
  const userName = getActiveUserName();
  if (!userName)
    return { ok: false, reason: "no_user", remaining: outboxCount() };

  const sending = readOutbox();
  const keys = Object.keys(sending);
  if (keys.length === 0)
    return { ok: true, sent: 0, remaining: 0, quarantined: 0 };

  const deviceId = getDeviceId();
  const sentKeys = [];
  const rejectedKeys = [];

  try {
    // Fast path: the whole outbox in one delta, with a timeout so a stalled
    // network can't wedge the UI on "Syncing" forever.
    await withTimeout(
      convex.mutation(convexRefs.applyChanges, {
        token: getActiveToken(),
        userName,
        changes: outboxToChanges(sending),
        deviceId,
      }),
      SYNC_CALL_TIMEOUT_MS,
    );
    sentKeys.push(...keys);
  } catch (batchError) {
    if (isTransientSyncError(batchError)) throw batchError; // retry later, outbox intact
    // Server rejected the batch. Isolate the poison: send entries one by one.
    console.error(
      "Sync batch rejected by server; isolating entries:",
      batchError,
    );
    for (const k of keys) {
      try {
        await withTimeout(
          convex.mutation(convexRefs.applyChanges, {
            token: getActiveToken(),
            userName,
            changes: outboxToChanges({ [k]: sending[k] }),
            deviceId,
          }),
          SYNC_CALL_TIMEOUT_MS,
        );
        sentKeys.push(k);
      } catch (entryError) {
        // Network died mid-isolation: stop and retry the rest next cycle.
        // (Already-sent entries re-send harmlessly — LWW is idempotent.)
        if (isTransientSyncError(entryError)) throw entryError;
        console.error(`Sync entry rejected (quarantining): ${k}`, entryError);
        rejectedKeys.push(k);
      }
    }
    if (rejectedKeys.length) {
      appendQuarantine(
        rejectedKeys.map((k) => ({
          quarantinedAt: getCurrentTimestamp(),
          error: String((batchError && batchError.message) || batchError),
          entry: sending[k],
        })),
      );
    }
  }

  // Prune sent + quarantined entries that weren't re-edited mid-flight.
  // (Quarantined entries live on under QUARANTINE_KEY — nothing is destroyed.)
  const fresh = readOutbox();
  for (const k of [...sentKeys, ...rejectedKeys]) {
    if (fresh[k] && fresh[k].updatedAt === sending[k].updatedAt)
      delete fresh[k];
  }
  writeOutbox(fresh);
  return {
    ok: true,
    sent: sentKeys.length,
    quarantined: rejectedKeys.length,
    remaining: Object.keys(fresh).length,
  };
}

/** Pull remote changes since the stored cursor. Returns the raw delta + serverTime. */
export async function fetchChangesSince() {
  const convex = getConvexClient();
  if (!convex) throw new Error("Convex is not configured");
  const userName = getActiveUserName();
  if (!userName) throw new Error("No authenticated user for delta pull");
  const cursor = getSyncCursor();
  return await withTimeout(
    convex.query(convexRefs.getChangesSince, {
      token: getActiveToken(),
      userName,
      ...(cursor ? { cursor } : {}),
    }),
    SYNC_CALL_TIMEOUT_MS,
  );
}
